import { encodeDate, encodeTime } from '@campfire/hot-date';
import { TabletButton } from '@campfire/tablet-button';
import { LinearProgressOverlay } from '@campfire/linear-progress-overlay';
import { Box, Typography } from '@material-ui/core';
import { Form, Formik, useFormikContext } from 'formik';
import { DateTime } from 'luxon';
import React, { MutableRefObject, useCallback, useEffect, useMemo, useState } from 'react';
import { useHistory } from 'react-router';
import { StringParam, useQueryParam } from 'use-query-params';
import { array as YupArray, date as YupDate, object as YupObject, string as YupString } from 'yup';
import { DailyRecurrence } from '../../../../../common/recurrence/__generated__/DailyRecurrence';
import { useCampfireTheme } from '../../../../../theme/useCampfireTheme';
import { useCampfireQuery } from '../../../../../global/network/useCampfireQuery';
import { arrayHead } from '../../../../../common/functions/array-head';
import { useSnackbar } from '../../../../../global/config/useSnackbar';
import { ActivityFormValues, ActivityTypeSelection, parseRecurrence, ActivityFormSession } from '../ActivityFormV2';
import { useSaveActivityFetch, NO_REPORT_NO_IMPACT, NO_REPORT_IMPACT_TRACKED } from '../activity-form-actions-v2';
import { CREATE_ACTIVITY_GET_PROGRAMS_V2, GET_ACTIVITY_MANAGEMENT_ACTIVITY_TAGS } from '../activity-form-model-v2.gql';
import {
  CreateActivityGetProgramsV2,
  CreateActivityGetProgramsV2Variables,
} from '../__generated__/CreateActivityGetProgramsV2';
import { GetActivityManagementActivityTags } from '../__generated__/GetActivityManagementActivityTags';
import { ActivityTypeStep } from './ActivityTypeStep';
import { ActivityDetailsStep } from './ActivityDetailsStep';
import { ScheduleSessionsStep } from './ScheduleSessionsStep';
import { PrivacyTeamStep } from './PrivacyTeamStep';
import { AdvancedStep } from './AdvancedStep';
import { TeamStep } from './TeamStep';

const DEFAULT_RETURN_SLUG = '/management/activity-management';

const STEP_LABELS = [
  'Activity Type',
  'Details',
  'Schedule & Sessions',
  'Privacy & Team',
  'Add Team',
  'Review & Create',
];

const getValidationSchema = (step: number, activityType: string) => {
  if (step === 1) {
    return YupObject().shape({
      activityName: YupString().required('Please provide a name'),
      programId: YupString().required('Please select a program'),
    });
  }
  if (step === 2) {
    const shape: any = {
      sessions: YupArray().min(1, 'At least 1 session is required'),
    };
    if (activityType === 'regular' || activityType === 'once-off' || activityType === 'event') {
      shape.startDate = YupDate()
        .nullable()
        .required('Please select a start date');
    }
    if (activityType === 'event') {
      shape.endDate = YupDate()
        .nullable()
        .required('Please select an end date');
    }
    return YupObject().shape(shape);
  }
  return undefined;
};

const ValuesSync = ({ onValuesChange }: { onValuesChange: (values: ActivityFormValues) => void }) => {
  const { values } = useFormikContext<ActivityFormValues>();
  useEffect(() => {
    onValuesChange(values);
  }, [values, onValuesChange]);
  return null;
};

interface CreateActivityWizardProps {
  sharedValuesRef: MutableRefObject<Partial<ActivityFormValues>>;
  viewModeToggle?: React.ReactNode;
}

export const CreateActivityWizard = ({ sharedValuesRef, viewModeToggle }: CreateActivityWizardProps) => {
  const [activeStep, setActiveStep] = useState(0);
  const [returnSlug] = useQueryParam('return', StringParam);
  const [selectedProgramId] = useQueryParam('programId', StringParam);
  const history = useHistory();
  const { theme } = useCampfireTheme();
  const { setSnackbar } = useSnackbar();

  const { data: programsData, loading: programsLoading } = useCampfireQuery<
    CreateActivityGetProgramsV2,
    CreateActivityGetProgramsV2Variables
  >(CREATE_ACTIVITY_GET_PROGRAMS_V2, { options: { variables: { activityId: '' } } });

  const { data: tagsData } = useCampfireQuery<GetActivityManagementActivityTags, {}>(
    GET_ACTIVITY_MANAGEMENT_ACTIVITY_TAGS
  );

  const programs = useMemo(
    () =>
      (programsData?.vm.programs ?? []).filter((p) => !p.dateSuspended).sort((a, b) => a.name.localeCompare(b.name)),
    [programsData]
  );
  const activityTags = useMemo(() => tagsData?.vm.activityTags ?? [], [tagsData]);

  const saveActivity = useSaveActivityFetch();

  const syncValues = useCallback(
    (values: ActivityFormValues) => {
      // eslint-disable-next-line no-param-reassign
      sharedValuesRef.current = values;
    },
    [sharedValuesRef]
  );

  const isLastStep = activeStep === STEP_LABELS.length - 1;

  const handleBack = () => {
    if (activeStep === 0) {
      history.replace(returnSlug ?? DEFAULT_RETURN_SLUG);
      return;
    }
    setActiveStep(activeStep - 1);
  };

  const getReturnPath = (activityId?: string) => {
    const base = returnSlug ?? DEFAULT_RETURN_SLUG;
    const activityIdParam = activityId ? `activityId=${activityId}` : '';
    const programIdParam = selectedProgramId ? `programId=${selectedProgramId}` : '';
    const alreadyHasParams = base.includes('?');
    const extraParams = alreadyHasParams
      ? `&${activityIdParam}&${programIdParam}`
      : `?${activityIdParam}&${programIdParam}`;
    return `${base}${extraParams}`;
  };

  const handleSubmitActivity = (values: ActivityFormValues) => {
    const startDate = values.startDate ? DateTime.fromJSDate(values.startDate) : DateTime.local();
    const endDate = values.endDate ? DateTime.fromJSDate(values.endDate) : undefined;
    const recurrence =
      values.frequency === 'custom' ? values.recurrence : values.frequency ? parseRecurrence(values) : undefined;
    const activityLocation = values.location
      ? {
          activityLocationId: values.location.activityLocationId,
          placesAddress: values.location,
          timeZone: startDate.zone.name,
          comments: '',
        }
      : null;

    const sessions = values.sessions.map((session: ActivityFormSession) => ({
      sessionId: session.sessionId || undefined,
      name: session.name,
      description: session.description,
      minVolunteers: session.minVolunteers,
      maxVolunteers: session.maxVolunteers || undefined,
      startTime: session.startTime ? encodeTime(DateTime.fromJSDate(session.startTime)) : null,
      endTime: session.endTime ? encodeTime(DateTime.fromJSDate(session.endTime)) : null,
      reportTypeId:
        session.reportType.reportTypeId === NO_REPORT_IMPACT_TRACKED ||
        session.reportType.reportTypeId === NO_REPORT_NO_IMPACT
          ? null
          : session.reportType.reportTypeId,
      activityLocation: session.activityLocation
        ? {
            activityLocationId: session.activityLocation.activityLocationId,
            placesAddress: session.activityLocation,
            timeZone: startDate.zone.name,
            comments: '',
          }
        : null,
      autoReport: Boolean(session.autoReport),
      isFlexible: Boolean(session.isFlexible),
      requiredRoleIds: session.requiredRoles?.map((r) => r.activityRoleId) || [],
    }));

    saveActivity
      .run({
        isRestrictedActivity: values.isRestrictedActivity,
        isHidden: values.isHidden,
        hasOpenRoster: values.hasOpenRoster,
        requireRosterTemplateApproval: values.requireRosterTemplateApproval,
        maxTeam: values.maxTeam,
        allowCICO: values.allowCICO,
        allowIndividualShiftTimes: values.allowIndividualShiftTimes,
        allowRosterComments: values.allowRosterComments,
        addressRevealBeforeDays: values.addressRevealBeforeDays,
        addressHideAfterDays: values.addressHideAfterDays,
        addressHidingExempt: values.addressHidingExempt,
        programId: values.programId,
        name: values.activityName,
        description: JSON.stringify(values.activityDescription),
        startDate: encodeDate(startDate),
        endDate: endDate ? encodeDate(endDate) : undefined,
        activityLocation: values.locationType === 'local' ? activityLocation : null,
        remoteLocation: values.locationType === 'remote' ? values.remoteLocation ?? undefined : undefined,
        recurrences: recurrence ? [recurrence] : undefined,
        sessions,
        addedAttachments: values.addedAttachments,
        attachmentCreationTokens: values.creationTokens,
        removedAttachmentIds: values.removedAttachmentIds,
        activityTagIds: values.activityTagIds,
        isEvent: values.isEvent || undefined,
        eventDates:
          values.isEvent && values.eventDates.length > 0
            ? values.eventDates.map((d) => encodeDate(DateTime.fromJSDate(d)))
            : undefined,
        invitedVolunteerIds:
          values.invitedVolunteers.length > 0 ? values.invitedVolunteers.map((v) => v.volunteerId) : undefined,
        activityLeaderIds:
          values.invitedVolunteers.filter((v) => v.isActivityLeader).length > 0
            ? values.invitedVolunteers.filter((v) => v.isActivityLeader).map((v) => v.volunteerId)
            : undefined,
      })
      .then((res) => {
        if (res.ok) {
          history.replace(getReturnPath(res.data.data.activityId));
          setSnackbar({ open: true, message: 'Activity saved', variant: 'success' });
          return;
        }
        setSnackbar({ open: true, message: 'Unable to save activity', variant: 'error' });
      })
      .catch(() => {
        setSnackbar({ open: true, message: 'Unable to save activity', variant: 'error' });
      });
  };

  const defaultProgramId =
    selectedProgramId && selectedProgramId !== 'all' ? selectedProgramId : arrayHead(programs)?.programId ?? '';

  const initialValues: ActivityFormValues = {
    activityId: '',
    activityType: (sharedValuesRef.current.activityType as ActivityTypeSelection) ?? 'regular',
    isEvent: sharedValuesRef.current.isEvent ?? sharedValuesRef.current.activityType === 'event',
    activityName: sharedValuesRef.current.activityName ?? '',
    activityDescription: sharedValuesRef.current.activityDescription ?? '',
    isRestrictedActivity: sharedValuesRef.current.isRestrictedActivity ?? true,
    isHidden: sharedValuesRef.current.isHidden ?? false,
    hasOpenRoster: sharedValuesRef.current.hasOpenRoster ?? false,
    requireRosterTemplateApproval: sharedValuesRef.current.requireRosterTemplateApproval ?? false,
    maxTeam: sharedValuesRef.current.maxTeam ?? null,
    allowCICO: sharedValuesRef.current.allowCICO ?? null,
    allowIndividualShiftTimes: sharedValuesRef.current.allowIndividualShiftTimes ?? null,
    allowRosterComments: sharedValuesRef.current.allowRosterComments ?? null,
    addressRevealBeforeDays: sharedValuesRef.current.addressRevealBeforeDays ?? null,
    addressHideAfterDays: sharedValuesRef.current.addressHideAfterDays ?? null,
    addressHidingExempt: sharedValuesRef.current.addressHidingExempt ?? null,
    programId: sharedValuesRef.current.programId || defaultProgramId,
    startDate: sharedValuesRef.current.startDate ?? null,
    endDate: sharedValuesRef.current.endDate ?? null,
    frequency:
      sharedValuesRef.current.frequency ??
      (sharedValuesRef.current.activityType === 'regular'
        ? 'daily'
        : sharedValuesRef.current.activityType === 'once-off'
        ? 'no-repeat'
        : undefined),
    locationType: sharedValuesRef.current.locationType ?? 'local',
    remoteLocation: sharedValuesRef.current.remoteLocation ?? { details: '' },
    location: sharedValuesRef.current.location ?? null,
    addedAttachments: sharedValuesRef.current.addedAttachments ?? [],
    creationTokens: sharedValuesRef.current.creationTokens ?? [],
    removedAttachmentIds: sharedValuesRef.current.removedAttachmentIds ?? [],
    sessions: sharedValuesRef.current.sessions ?? [],
    activityTagIds: sharedValuesRef.current.activityTagIds ?? [],
    recurrence:
      sharedValuesRef.current.recurrence ??
      ({
        __typename: 'DailyRecurrenceType',
        dayInterval: 1,
        startDate: DateTime.local().toISODate(),
        endDate: null,
        humanReadable: '',
      } as DailyRecurrence),
    eventDates: sharedValuesRef.current.eventDates ?? [],
    invitedVolunteers: sharedValuesRef.current.invitedVolunteers ?? [],
  };

  const buttonStyle = {
    borderRadius: 4,
    textTransform: 'none' as const,
    padding: '7px 12px',
    fontWeight: 600,
    fontSize: 13,
  };

  return (
    <Box display='flex' flexDirection='column' height='100%'>
      <Formik
        enableReinitialize
        initialValues={initialValues}
        validationSchema={getValidationSchema(activeStep, initialValues.activityType)}
        onSubmit={(values, actions) => {
          if (isLastStep) {
            handleSubmitActivity(values);
          } else {
            setActiveStep(activeStep + 1);
          }
          actions.setTouched({});
          actions.setSubmitting(false);
        }}
      >
        {() => (
          <Form
            style={{ display: 'flex', flexDirection: 'column', flex: 1, overflow: 'hidden' }}
            onKeyDown={(e) => {
              if (
                (e.key === 'Enter' || e.key === ' ') &&
                (e.target as HTMLElement).tagName !== 'TEXTAREA' &&
                (e.target as HTMLElement).tagName !== 'INPUT'
              ) {
                e.preventDefault();
              }
            }}
          >
            <ValuesSync onValuesChange={syncValues} />
            <LinearProgressOverlay isLoading={programsLoading || saveActivity.isLoading} />

            <Box flex={1} overflow='auto' px={8} pt={3} pb={4}>
              <Typography style={{ fontSize: 22, fontWeight: 600, marginBottom: 16 }}>Create Activity</Typography>

              {activeStep === 0 && <ActivityTypeStep />}
              {activeStep === 1 && <ActivityDetailsStep programs={programs} activityTags={activityTags} />}
              {activeStep === 2 && <ScheduleSessionsStep />}
              {activeStep === 3 && <PrivacyTeamStep />}
              {activeStep === 4 && <TeamStep />}
              {activeStep === 5 && <AdvancedStep />}
            </Box>

            <Box
              display='flex'
              justifyContent='flex-end'
              alignItems='center'
              paddingY={2}
              paddingX={4}
              paddingRight={12}
              borderTop={`1px solid ${theme.color.grey.border}`}
              boxShadow='0 -2px 8px rgba(0,0,0,0.06)'
            >
              {viewModeToggle}
              <Typography variant='body2' style={{ marginRight: 'auto', color: '#808080', fontWeight: 600 }}>
                Step {activeStep + 1} of {STEP_LABELS.length}: {STEP_LABELS[activeStep]}
              </Typography>
              <TabletButton
                size='medium'
                onClick={handleBack}
                style={{
                  ...buttonStyle,
                  backgroundColor: theme.color.grey.neutral200,
                  color: '#ffffff',
                  marginRight: 8,
                }}
              >
                Back
              </TabletButton>
              <TabletButton
                size='medium'
                variant='text'
                type='submit'
                disabled={saveActivity.isLoading}
                style={{
                  ...buttonStyle,
                  backgroundColor: theme.color.secondary.main900,
                  color: '#ffffff',
                }}
              >
                {isLastStep ? 'Create Activity' : 'Next'}
              </TabletButton>
            </Box>
          </Form>
        )}
      </Formik>
    </Box>
  );
};
